import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Star, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { fieldErrors, issuesOf } from '../../api/errors';
import { orgApi, refApi } from '../../api/endpoints';
import { currenciesQuery, organizationQuery, queryKeys } from '../../api/queries';
import type { ActivityCode } from '../../api/types';
import { useI18n } from '../../i18n/I18nProvider';
import { Button } from '../../ui/Button';
import { AsyncComboBox, ComboBoxField } from '../../ui/ComboBox';
import { ModalDialog } from '../../ui/Dialog';
import { Callout } from '../../ui/Feedback';
import { TextField } from '../../ui/TextField';
import { useToast } from '../../ui/Toast';
import { AddressFields, emptyAddress, toAddressInput, type AddressState } from '../address';
import { CustomTaxDialog } from '../items/ItemDialog';
import styles from '../sales/Sales.module.css';

type DialogProps = { orgId: string; isOpen: boolean; onOpenChange: (open: boolean) => void };

function useInvalidateOrg(orgId: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.org(orgId) });
}

/** RIN, e-mail and phone of the issuer (2.05 company block). */
export function ProfileDialog({ orgId, isOpen, onOpenChange }: DialogProps) {
  const { t } = useI18n();
  const toast = useToast();
  const invalidate = useInvalidateOrg(orgId);
  const org = useQuery(organizationQuery(orgId));
  const [rin, setRin] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  useEffect(() => {
    if (isOpen && org.data) {
      setRin(org.data.rin ?? '');
      setEmail(org.data.email ?? '');
      setPhone(org.data.phone ?? '');
    }
  }, [isOpen, org.data]);
  const save = useMutation({
    mutationFn: () =>
      orgApi.update(orgId, {
        version: org.data?.version ?? 1,
        rin: rin.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
      }),
    onSuccess: async () => {
      toast.success(t.setup.saved);
      await invalidate();
      onOpenChange(false);
    },
  });
  const errors = fieldErrors(t, issuesOf(save.error));
  return (
    <ModalDialog isOpen={isOpen} onOpenChange={onOpenChange} title={t.setup.profileTitle}>
      <form
        className={styles.formStack}
        onSubmit={(e) => (e.preventDefault(), save.mutate())}
        data-testid="profile-dialog"
      >
        <TextField
          label={t.setup.rin}
          fieldName="rin"
          value={rin}
          onChange={setRin}
          dir="ltr"
          errorMessage={errors.rin}
        />
        <TextField
          label={t.setup.email}
          fieldName="email"
          value={email}
          onChange={setEmail}
          dir="ltr"
          errorMessage={errors.email}
        />
        <TextField
          label={t.setup.phone}
          fieldName="phone"
          value={phone}
          onChange={setPhone}
          dir="ltr"
          errorMessage={errors.phone}
        />
        <div className={styles.dialogFooter}>
          <Button variant="secondary" onPress={() => onOpenChange(false)}>
            {t.common.cancel}
          </Button>
          <Button variant="primary" type="submit" isPending={save.isPending} testId="profile-save">
            {t.setup.save}
          </Button>
        </div>
      </form>
    </ModalDialog>
  );
}

/** The issuing branch (ETA issuer address). */
export function BranchDialog({ orgId, isOpen, onOpenChange }: DialogProps) {
  const { t } = useI18n();
  const toast = useToast();
  const invalidate = useInvalidateOrg(orgId);
  const [etaBranchId, setEtaBranchId] = useState('0');
  const [name, setName] = useState('');
  const [address, setAddress] = useState<AddressState>(emptyAddress());
  const save = useMutation({
    mutationFn: () => {
      const a = toAddressInput(address);
      return orgApi.createBranch(orgId, {
        etaBranchId: etaBranchId.trim(),
        name: name.trim(),
        ...a,
        country: a.country || 'EG',
      });
    },
    onSuccess: async () => {
      toast.success(t.setup.saved);
      await invalidate();
      onOpenChange(false);
    },
  });
  const errors = fieldErrors(t, issuesOf(save.error));
  return (
    <ModalDialog isOpen={isOpen} onOpenChange={onOpenChange} title={t.setup.branchTitle} wide>
      <form
        className={styles.formStack}
        onSubmit={(e) => (e.preventDefault(), save.mutate())}
        data-testid="branch-dialog"
      >
        <div className={styles.fieldGrid}>
          <TextField
            label={t.setup.etaBranchId}
            description={t.setup.etaBranchIdHint}
            fieldName="etaBranchId"
            value={etaBranchId}
            onChange={setEtaBranchId}
            dir="ltr"
            isRequired
            errorMessage={errors.etaBranchId}
          />
          <TextField
            label={t.setup.branchName}
            fieldName="name"
            value={name}
            onChange={setName}
            isRequired
            errorMessage={errors.name}
          />
        </div>
        <AddressFields value={address} onChange={setAddress} errors={errors} />
        <div className={styles.dialogFooter}>
          <Button variant="secondary" onPress={() => onOpenChange(false)}>
            {t.common.cancel}
          </Button>
          <Button variant="primary" type="submit" isPending={save.isPending} testId="branch-save">
            {t.setup.addBranch}
          </Button>
        </div>
      </form>
    </ModalDialog>
  );
}

/** Activity codes (set once; changing them later needs a reviewed migration path). */
export function ActivityDialog({ orgId, isOpen, onOpenChange }: DialogProps) {
  const { t, pick } = useI18n();
  const toast = useToast();
  const invalidate = useInvalidateOrg(orgId);
  const [codes, setCodes] = useState<ActivityCode[]>([]);
  const [primary, setPrimary] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      orgApi.setActivityCodes(orgId, {
        codes: codes.map((c) => c.code),
        primary: primary ?? codes[0]?.code ?? '',
      }),
    onSuccess: async () => {
      toast.success(t.setup.saved);
      await invalidate();
      onOpenChange(false);
    },
  });
  return (
    <ModalDialog isOpen={isOpen} onOpenChange={onOpenChange} title={t.setup.activityTitle} wide>
      <div className={styles.formStack} data-testid="activity-dialog">
        <Callout tone="info" role="note">
          {t.setup.activityLocked}
        </Callout>
        <AsyncComboBox<ActivityCode>
          label={t.setup.activitySearch}
          fieldName="activitySearch"
          value={null}
          selectedItem={null}
          clearOnPick
          onChange={(c) => {
            if (!c || codes.some((x) => x.code === c.code)) return;
            setCodes([...codes, c]);
            if (!primary) setPrimary(c.code);
          }}
          queryFor={(q) => ({
            queryKey: ['ref', 'activity-codes', q],
            queryFn: ({ signal }) => refApi.activityCodes(q, signal),
          })}
          getKey={(c) => c.code}
          getLabel={(c) => `${c.code} — ${pick(c.nameAr, c.nameEn)}`}
        />
        <div className={styles.chips}>
          {codes.map((c) => (
            <span key={c.code} className={styles.chip}>
              {c.code === primary ? <Star size={12} aria-label={t.setup.primary} /> : null}
              {c.code} — {pick(c.nameAr, c.nameEn)}
              {c.code !== primary ? (
                <button
                  type="button"
                  onClick={() => setPrimary(c.code)}
                  aria-label={t.setup.makePrimary}
                >
                  <Star size={12} />
                </button>
              ) : null}
              <button
                type="button"
                aria-label={t.setup.remove}
                onClick={() => {
                  setCodes(codes.filter((x) => x.code !== c.code));
                  if (primary === c.code) setPrimary(null);
                }}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
        {save.isError ? <Callout tone="danger">{t.errors.codes.validation_error}</Callout> : null}
        <div className={styles.dialogFooter}>
          <Button variant="secondary" onPress={() => onOpenChange(false)}>
            {t.common.cancel}
          </Button>
          <Button
            variant="primary"
            isDisabled={codes.length === 0}
            isPending={save.isPending}
            onPress={() => save.mutate()}
            testId="activity-save"
          >
            {t.setup.saveActivities}
          </Button>
        </div>
      </div>
    </ModalDialog>
  );
}

/** Taxes: the VAT 14% preset (official subtype V009) or a custom one. */
export function TaxDialog({ orgId, isOpen, onOpenChange }: DialogProps) {
  const { t } = useI18n();
  const toast = useToast();
  const invalidate = useInvalidateOrg(orgId);
  const [custom, setCustom] = useState(false);
  const add = useMutation({
    mutationFn: (input: {
      taxType: string;
      taxSubtype: string;
      rate?: string;
      nameAr: string;
      nameEn: string;
    }) => orgApi.createTax(orgId, input),
    onSuccess: async () => {
      toast.success(t.setup.saved);
      await invalidate();
      onOpenChange(false);
    },
    onError: (e) => toast.error(e),
  });
  return (
    <ModalDialog isOpen={isOpen} onOpenChange={onOpenChange} title={t.setup.taxTitle}>
      <div className={styles.formStack} data-testid="tax-dialog">
        <Button
          variant="primary"
          isPending={add.isPending}
          testId="tax-vat-preset"
          onPress={() =>
            add.mutate({
              taxType: 'T1',
              taxSubtype: 'V009',
              rate: '14',
              nameAr: 'ضريبة القيمة المضافة 14٪',
              nameEn: 'VAT 14%',
            })
          }
        >
          {t.setup.vatPreset}
        </Button>
        <Button variant="secondary" onPress={() => setCustom(true)} testId="tax-custom">
          {t.catalogue.addCustomTax}
        </Button>
      </div>
      <CustomTaxDialog
        orgId={orgId}
        isOpen={custom}
        onOpenChange={setCustom}
        onCreated={() => {
          void invalidate();
          onOpenChange(false);
        }}
      />
    </ModalDialog>
  );
}

/** Document currencies (M-04: a USD invoice needs USD enabled). */
export function CurrencyDialog({ orgId, isOpen, onOpenChange }: DialogProps) {
  const { t, pick } = useI18n();
  const toast = useToast();
  const invalidate = useInvalidateOrg(orgId);
  const ref = useQuery({ ...currenciesQuery, enabled: isOpen });
  const [code, setCode] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => orgApi.addCurrency(orgId, code ?? ''),
    onSuccess: async () => {
      toast.success(t.setup.saved);
      await invalidate();
      onOpenChange(false);
    },
    onError: (e) => toast.error(e),
  });
  return (
    <ModalDialog isOpen={isOpen} onOpenChange={onOpenChange} title={t.setup.currencyTitle}>
      <div className={styles.formStack} data-testid="currency-dialog">
        <ComboBoxField
          label={t.editor.settings.currency}
          fieldName="currencyCode"
          value={code}
          onChange={setCode}
          options={(ref.data ?? []).map((c) => ({
            id: c.code,
            label: `${c.code} — ${pick(c.nameAr, c.nameEn)}`,
          }))}
        />
        <div className={styles.dialogFooter}>
          <Button variant="secondary" onPress={() => onOpenChange(false)}>
            {t.common.cancel}
          </Button>
          <Button
            variant="primary"
            isDisabled={!code}
            isPending={add.isPending}
            onPress={() => add.mutate()}
            testId="currency-save"
          >
            {t.setup.addCurrency}
          </Button>
        </div>
      </div>
    </ModalDialog>
  );
}
